import { useEffect, useRef, useState } from "react";

type Tilt = { beta: number; gamma: number };

type DeviceOrientationEventCtor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

export function useGyro(active: boolean) {
  const [tilt, setTilt] = useState<Tilt>({ beta: 0, gamma: 0 });
  const [permission, setPermission] = useState<"unknown" | "granted" | "denied" | "unavailable">(
    "unknown",
  );
  const handlerRef = useRef<((e: DeviceOrientationEvent) => void) | null>(null);

  useEffect(() => {
    if (!active) return;
    if (typeof window === "undefined" || typeof DeviceOrientationEvent === "undefined") {
      setPermission("unavailable");
      return;
    }

    const handler = (e: DeviceOrientationEvent) => {
      setTilt({ beta: e.beta ?? 0, gamma: e.gamma ?? 0 });
    };
    handlerRef.current = handler;

    const ctor = DeviceOrientationEvent as DeviceOrientationEventCtor;
    if (typeof ctor.requestPermission === "function") {
      // iOS — must be triggered by user gesture; defer until requestPermission() is called
      setPermission("unknown");
    } else {
      window.addEventListener("deviceorientation", handler);
      setPermission("granted");
    }

    return () => {
      if (handlerRef.current) window.removeEventListener("deviceorientation", handlerRef.current);
    };
  }, [active]);

  const requestPermission = async () => {
    const ctor = DeviceOrientationEvent as DeviceOrientationEventCtor;
    if (typeof ctor.requestPermission !== "function") return permission;
    try {
      const result = await ctor.requestPermission();
      setPermission(result);
      if (result === "granted" && handlerRef.current) {
        window.addEventListener("deviceorientation", handlerRef.current);
      }
      return result;
    } catch {
      setPermission("denied");
      return "denied" as const;
    }
  };

  return { tilt, permission, requestPermission };
}
